export type NotificationState = "unsupported" | "default" | "granted" | "denied";

export function isPushSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "Notification" in window &&
    "PushManager" in window
  );
}

export function getNotificationState(): NotificationState {
  if (!isPushSupported()) return "unsupported";
  return Notification.permission as NotificationState;
}

export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as any).standalone === true
  );
}

export function isIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  // iPhone / iPod / iPad. iPadOS 13+ reports as "Macintosh" but is a touch
  // device, so treat a touch-capable Mac UA as iPad too.
  return (
    /iPhone|iPad|iPod/.test(ua) ||
    (/Macintosh/.test(ua) && typeof document !== "undefined" && "ontouchend" in document)
  );
}

export function isIOSSafari(): boolean {
  if (!isIOS()) return false;
  const ua = navigator.userAgent;
  // All iOS browsers use WebKit, but only Safari can Add-to-Home-Screen and
  // run web push. Exclude the other iOS browsers (Chrome, Firefox, Edge,
  // Opera, Google app) so we only show A2HS guidance where it actually works.
  return !/CriOS|FxiOS|EdgiOS|OPiOS|GSA/.test(ua);
}

async function persistSubscription(reg: ServiceWorkerRegistration): Promise<boolean> {
  const vapidKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;
  if (!vapidKey) return false;

  const existing = await reg.pushManager.getSubscription();
  const subscription =
    existing ||
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: vapidKey,
    }));

  const res = await fetch("/api/user/push-subscription", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ subscription: subscription.toJSON() }),
    credentials: "include",
  });
  if (!res.ok) {
    throw new Error(`Push subscription registration failed: ${res.status}`);
  }
  return true;
}

/**
 * Register the push subscription with the server. Assumes permission is
 * already granted — does NOT prompt. Safe to call on startup for returning
 * users. Returns true if a subscription was registered.
 */
export async function syncExistingSubscription(): Promise<boolean> {
  try {
    if (!isPushSupported()) return false;
    if (Notification.permission !== "granted") return false;
    const reg = await navigator.serviceWorker.ready;
    return await persistSubscription(reg);
  } catch (err) {
    console.warn("[Push] Subscription sync failed:", err);
    return false;
  }
}

/**
 * Request notification permission (must be called from a user gesture) and,
 * if granted, register the push subscription with the server. Returns the
 * resulting permission state. Throws if permission was granted but the
 * subscription could not be registered with the server, so callers can show
 * an error instead of silently assuming success.
 */
export async function enablePush(): Promise<NotificationState> {
  if (!isPushSupported()) return "unsupported";

  const permission = await Notification.requestPermission();
  if (permission !== "granted") return permission as NotificationState;

  const reg = await navigator.serviceWorker.ready;
  await persistSubscription(reg);
  return "granted";
}
